import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAppContext } from '../../../Store/Store';

interface CodeScannerPistolScreenProps {
  onNext: () => void;
  showSkipNextScreenButton?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
}

export default function CodeScannerPistolScreen({
  onNext,
  showSkipNextScreenButton = false,
  showBackButton = false,
  onBack,
}: CodeScannerPistolScreenProps) {
  const { backendBaseUrl, redirectBaseUrl, product, event, style } =
    useAppContext();
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'ready' | 'posting'
  >('idle');
  const [error, setError] = useState<string>('');
  const [fieldKey, setFieldKey] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const sentRef = useRef(false);

  const buttonStyles: React.CSSProperties = useMemo(
    () => ({
      color: style?.codescanner_pistol_button_text_color || 'white',
      background:
        style?.codescanner_pistol_button_bg_color ||
        'linear-gradient(to right, #6991F8 0%, #07296a 100%)',
      boxShadow: `0 5px 5px ${style?.codescanner_pistol_button_shadow_color || '#FFFFFF'}`,
      border: 'none',
      cursor: 'pointer',
      padding: '12px 24px',
      fontSize: '14px',
      fontWeight: 'bold',
      borderRadius: 4,
      minHeight: 48,
    }),
    [style]
  );

  const inputStyles: React.CSSProperties = useMemo(
    () => ({
      width: '100%',
      maxWidth: 400,
      padding: '12px 16px',
      fontSize: '16px',
      border: '2px solid #ddd',
      borderRadius: 8,
      outline: 'none',
      textAlign: 'center' as const,
      fontFamily: 'monospace',
      letterSpacing: '2px',
    }),
    []
  );

  const fetchConfig = useCallback(async () => {
    if (!event?.id) return;
    try {
      const url = `${backendBaseUrl}/codescanner/config`;
      console.log('[PISTOL][CONFIG][GET]', url, { event_id: event.id });
      const resp = await axios.get(url, { params: { event_id: event.id } });
      console.log('[PISTOL][CONFIG][RESP]', resp.status, resp.data);
      setFieldKey(resp.data.field_key || 'serial_number');
    } catch (e) {
      console.log('[PISTOL][CONFIG][ERROR]', e);
      setFieldKey('serial_number');
    }
  }, [backendBaseUrl, event?.id]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const postToBackend = useCallback(
    async (text: string) => {
      if (sentRef.current) return;
      sentRef.current = true;
      setStatus('posting');
      const payload = {
        product_id: product?.id || undefined,
        product_code: product?.code || undefined,
        field_key: fieldKey || 'serial_number',
        value: text,
        screen_type_key: 'codescannerpistol',
      };
      try {
        const url = `${backendBaseUrl}/codescanner/update`;
        console.log('[PISTOL][POST]', url, payload);
        const resp = await axios.post(url, payload);
        console.log('[PISTOL][POST][RESP]', resp.status, resp.data);
        const redirectCode = resp?.data?.redirect_product;
        if (redirectCode) {
          const qs = new URLSearchParams(window.location.search);
          const base = `${redirectBaseUrl}/?product=${encodeURIComponent(redirectCode)}&start_index=1`;
          const urlWithToken = qs.get('token')
            ? `${base}&token=${encodeURIComponent(qs.get('token') as string)}`
            : base;
          window.location.href = urlWithToken;
        }
        setStatus('ready');
        if (!redirectCode) onNext();
      } catch (e) {
        console.log('[PISTOL][ERROR] post', e);
        setError(
          style?.codescanner_pistol_error_indication || 'Errore invio dato'
        );
        setStatus('ready');
        sentRef.current = false;
      }
    },
    [
      backendBaseUrl,
      fieldKey,
      onNext,
      product?.code,
      product?.id,
      redirectBaseUrl,
      style?.codescanner_pistol_error_indication,
    ]
  );

  const handleSubmit = useCallback(async () => {
    if (!inputValue.trim()) {
      setError(
        style?.codescanner_pistol_error_indication || 'Inserire un valore'
      );
      return;
    }
    await postToBackend(inputValue.trim());
  }, [inputValue, postToBackend, style?.codescanner_pistol_error_indication]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  useEffect(() => {
    console.log('[PISTOL][MOUNT]');
    setStatus('ready');
    return () => {
      console.log('[PISTOL][UNMOUNT]');
    };
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'clamp(8px, 1.5vh, 16px)',
        width: '100%',
        padding: 'clamp(8px, 2vw, 16px)',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
          width: '100%',
          maxWidth: 500,
        }}
      >
        <input
          type="text"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={
            style?.codescanner_pistol_input_placeholder || 'Inserisci codice...'
          }
          style={inputStyles}
          disabled={status === 'posting'}
          autoFocus
        />

        <button
          onClick={handleSubmit}
          style={buttonStyles}
          disabled={status === 'posting' || !inputValue.trim()}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: style?.codescanner_pistol_button_indication || 'Invia',
            }}
          />
        </button>
      </div>

      {error && (
        <div
          style={{
            backgroundColor: '#ffe6e6',
            border: '2px solid #ff4444',
            borderRadius: 8,
            padding: 16,
            maxWidth: 400,
            width: '90%',
            textAlign: 'center',
          }}
        >
          <div
            style={{ marginBottom: 8 }}
            dangerouslySetInnerHTML={{ __html: error }}
          />
          <button
            onClick={() => setError('')}
            style={{
              backgroundColor: '#ff4444',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              padding: '8px 16px',
              cursor: 'pointer',
            }}
          >
            OK
          </button>
        </div>
      )}
      {showBackButton && (
        <button
          type="button"
          onClick={() => onBack?.()}
          style={{
            fontSize: '18px',
            textDecoration: 'underline',
            color: style?.back_button_text_color || '#07296A',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            marginTop: '20px',
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: style?.back_button_indication || 'Back',
            }}
          />
        </button>
      )}
      {showSkipNextScreenButton && (
        <button
          type="button"
          onClick={onNext}
          style={{
            fontSize: '18px',
            textDecoration: 'underline',
            color: style?.skip_next_screen_button_text_color || '#07296A',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            marginTop: '20px',
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: style?.skip_next_screen_button_indication || 'Skip',
            }}
          />
        </button>
      )}
    </div>
  );
}
